/**
 * The document trail behind a purchase order.
 *
 * SAP calls this the Document Flow, Oracle calls it drill-down. The idea is
 * old and good: from one document, show every other document connected to it,
 * in the order things happened, each one clickable.
 *
 * WHY IT IS A SPINE AND NOT A TABLE
 *
 * The relationship between these documents is sequential — the indent caused
 * the requisition, which caused the order, which caused the receipt, which
 * caused the payable. A table of "related documents" loses that, and the first
 * question anybody asks of a trail is "what happened, in what order".
 *
 * So it renders as a vertical spine with the order highlighted in the middle:
 * what led to it above, what followed from it below.
 *
 * READ-ONLY, DELIBERATELY
 *
 * Every row links to the document's own screen, where the approval chain, the
 * period lock and that document's permissions apply. Nothing can be actioned
 * from here — a trail that let you approve things would be a second, unguarded
 * route into documents that already have a guarded one.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRightLeft, ChevronDown, ChevronRight, ClipboardList, FileText, HardHat,
  Package, Paperclip, ReceiptIndianRupee, ReceiptText, Route, Wallet,
} from 'lucide-react'
import { Alert, Card, Loading, StatusPill } from '@/components/ui'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'
import { usePeople } from '@/lib/data'

type Trail = {
  po: any
  pr: any | null
  indent: any | null
  ists: any[]
  grns: any[]
  payables: any[]
  invoices: any[]
  credit_notes: any[]
  payments: any[]
  money: Record<string, number>
}

/** One row on the spine. */
function Node({
  icon, kind, to, title, meta, right, tone = 'normal',
}: {
  icon: React.ReactNode
  kind: string
  to?: string
  title: React.ReactNode
  meta?: React.ReactNode
  right?: React.ReactNode
  tone?: 'normal' | 'focus' | 'muted'
}) {
  const body = (
    <div className={[
      'flex items-start gap-3 rounded-lg border px-3 py-2 transition',
      tone === 'focus'
        ? 'border-oval-300 bg-oval-50/70 ring-1 ring-oval-200'
        : tone === 'muted'
          ? 'border-slate-200 bg-slate-50/60'
          : 'border-slate-200 bg-white hover:border-oval-200 hover:bg-oval-50/40',
    ].join(' ')}>
      <span className={tone === 'focus' ? 'mt-0.5 text-oval-700' : 'mt-0.5 text-slate-400'}>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{kind}</p>
        <div className="truncate text-sm font-semibold text-slate-800">{title}</div>
        {meta && <div className="mt-0.5 text-xs text-slate-500">{meta}</div>}
      </div>
      {right && <div className="shrink-0 text-right">{right}</div>}
    </div>
  )
  return to ? <Link to={to} className="block">{body}</Link> : body
}

/** The connector between two nodes. */
const Rail = () => (
  <div className="ml-[26px] h-3 w-px bg-slate-200" aria-hidden />
)

export function DocumentTrail({ poId }: { poId: string }) {
  const [open, setOpen] = useState(false)
  const [trail, setTrail] = useState<Trail | null>(null)
  const [loading, setLoading] = useState(false)
  const [denied, setDenied] = useState<string | null>(null)
  // The trail names people, not email addresses. The view behind it returns
  // approver_email; only the drawing changes.
  const people = usePeople()

  // WHY THIS DOES NOT DEPEND ON `loading`
  //
  // It used to, and the trail spun forever. The sequence was:
  //
  //   1. open becomes true -> the effect runs and calls setLoading(true)
  //   2. `loading` was in the dependency array, so React re-ran the effect
  //   3. re-running an effect first runs the PREVIOUS effect's cleanup,
  //      which set alive = false — on the only request in flight
  //   4. the new run hit the `|| loading` guard and returned without fetching
  //   5. the original response arrived, saw alive === false, and threw itself
  //      away. setLoading(false) never ran.
  //
  // A spinner that never stops, from an effect that cancelled its own request.
  // The guard state must not be in the deps: a ref remembers what has already
  // been fetched, and the effect depends only on the two things that genuinely
  // identify the work.
  const fetchedFor = useRef<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (fetchedFor.current === poId) return
    fetchedFor.current = poId

    let alive = true
    setLoading(true)
    setDenied(null)

    // A belt-and-braces timeout. If the request never settles at all — a
    // dropped 2G connection mid-call — the user gets an explanation rather
    // than a spinner they have to guess about.
    const timer = setTimeout(() => {
      if (!alive) return
      alive = false
      setLoading(false)
      setDenied('The trail took too long to load. Check the connection and press Show trail again.')
      fetchedFor.current = null
    }, 30000)

    void (async () => {
      const { data, error } = await supabase.rpc('rpc_po_trail', { p_po_id: poId })
      if (!alive) return
      clearTimeout(timer)
      setLoading(false)
      if (error) {
        setDenied(error.message)
        fetchedFor.current = null      // let them try again
        return
      }
      setTrail(data as Trail)
    })()

    return () => { alive = false; clearTimeout(timer) }
  }, [open, poId])

  // A different order on the same screen is a different trail.
  useEffect(() => {
    if (fetchedFor.current && fetchedFor.current !== poId) {
      fetchedFor.current = null
      setTrail(null)
      setDenied(null)
    }
  }, [poId])

  const m = trail?.money

  return (
    <Card
      title="Document trail"
      subtitle="Everything connected to this order, in the order it happened"
      actions={
        <button className="btn-secondary btn-sm" onClick={() => setOpen((v) => !v)}>
          {open ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
          {open ? 'Hide' : 'Show trail'}
        </button>
      }>

      {!open && (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Route className="h-4 w-4 text-slate-300" />
          The indent, requisition, transfers, goods receipts, invoices, credit notes and
          payments linked to this order.
        </p>
      )}

      {open && loading && <Loading label="Following the chain…" />}

      {open && denied && (
        <Alert tone="warning" title="The trail is not available to you">
          {denied}
        </Alert>
      )}

      {open && trail && (
        <>
          {/* ---------- the money, in one strip ---------- */}
          {m && (
            <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-6">
              {[
                ['Ordered', m.ordered, ''],
                ['Received', m.received, ''],
                ['Invoiced', m.invoiced, ''],
                ['Credit notes', m.credited, Number(m.credited) > 0 ? 'text-amber-700' : ''],
                ['Paid', m.paid, ''],
                ['Outstanding', m.outstanding,
                  Number(m.outstanding) > 0.01 ? 'text-red-700' : 'text-emerald-700'],
              ].map(([label, value, cls]) => (
                <div key={label as string} className="bg-white px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    {label as string}
                  </p>
                  <p className={`text-sm font-bold tabular-nums ${cls as string}`}>
                    {rupees(value as number)}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-0">
            {/* ---------- upstream ---------- */}
            {trail.indent && (
              <>
                <Node icon={<HardHat className="h-4 w-4" />} kind="Site indent"
                      to={`/indents/${trail.indent.id}`}
                      title={trail.indent.id}
                      meta={<>
                        {ddmmyyyy(trail.indent.indent_date)}
                        {trail.indent.raised_by_name && ` · raised by ${trail.indent.raised_by_name}`}
                        {trail.indent.work_description && ` · ${trail.indent.work_description}`}
                      </>}
                      right={<StatusPill status={trail.indent.status} />} />
                <Rail />
              </>
            )}

            {trail.pr ? (
              <>
                <Node icon={<ClipboardList className="h-4 w-4" />} kind="Purchase requisition"
                      to={`/prs/${trail.pr.id}`}
                      title={trail.pr.id}
                      meta={<>
                        {ddmmyyyy(trail.pr.date_created)}
                        {trail.pr.requested_by && ` · ${trail.pr.requested_by}`}
                        {trail.pr.approver_email
                          && ` · approved by ${people.name({ email: trail.pr.approver_email })}`}
                      </>}
                      right={<>
                        <StatusPill status={trail.pr.status} />
                        {trail.pr.total_estimated_amount != null && (
                          <p className="mt-0.5 text-xs tabular-nums text-slate-500">
                            est. {rupees(trail.pr.total_estimated_amount)}
                          </p>
                        )}
                      </>} />
                <Rail />
              </>
            ) : (
              <>
                <Node icon={<ClipboardList className="h-4 w-4" />} kind="Purchase requisition"
                      title="Raised directly, with no requisition"
                      meta="Legacy orders and direct purchases have no requisition behind them."
                      tone="muted" />
                <Rail />
              </>
            )}

            {trail.ists.map((t) => (
              <div key={t.id}>
                <Node icon={<ArrowRightLeft className="h-4 w-4" />} kind="Inter-site transfer"
                      to={`/ists/${t.id}`}
                      title={t.id}
                      meta={<>
                        {t.from_site_id} → {t.to_site_id}
                        {t.challan_no && ` · challan ${t.challan_no}`}
                        {t.delivered_at && ` · delivered ${ddmmyyyy(t.delivered_at)}`}
                        {' — part of the requisition met from a sister site, not by this order'}
                      </>}
                      right={<StatusPill status={t.status} />} />
                <Rail />
              </div>
            ))}

            {/* ---------- the order ---------- */}
            <Node icon={<FileText className="h-5 w-5" />} kind="This purchase order"
                  title={trail.po.id} tone="focus"
                  meta={<>
                    {ddmmyyyy(trail.po.date_created)} · {trail.po.vendor_name}
                    {trail.po.site_name && ` · deliver to ${trail.po.site_name}`}
                    {trail.po.delivery_date && ` · due ${ddmmyyyy(trail.po.delivery_date)}`}
                  </>}
                  right={<>
                    <StatusPill status={trail.po.status} />
                    <p className="mt-0.5 text-xs font-semibold tabular-nums text-slate-700">
                      {rupees(trail.po.total_amount)}
                    </p>
                  </>} />

            {/* ---------- downstream ---------- */}
            {trail.grns.length === 0 && (
              <>
                <Rail />
                <Node icon={<Package className="h-4 w-4" />} kind="Goods receipts"
                      title="Nothing received against this order yet" tone="muted" />
              </>
            )}
            {trail.grns.map((g) => (
              <div key={g.id}>
                <Rail />
                <Node icon={<Package className="h-4 w-4" />} kind="Goods receipt"
                      to={`/grns/${g.id}`}
                      title={g.id}
                      meta={<>
                        received {ddmmyyyy(g.received_date)}
                        {g.challan_no && ` · challan ${g.challan_no}`}
                        {g.approver_email
                          && ` · passed by ${people.name({ email: g.approver_email })}`}
                        {g.attachments > 0
                          ? ` · ${g.attachments} document(s)`
                          : ' · no paperwork attached'}
                      </>}
                      right={<>
                        <StatusPill status={g.status} />
                        <p className="mt-0.5 text-xs tabular-nums text-slate-600">
                          {rupees(g.value)}
                        </p>
                      </>} />
              </div>
            ))}

            {trail.payables.map((p) => (
              <div key={p.id}>
                <Rail />
                <Node icon={<Wallet className="h-4 w-4" />} kind="Payable"
                      to={`/payables/${p.id}`}
                      title={p.id}
                      meta={<>
                        {p.due_date && `due ${ddmmyyyy(p.due_date)} · `}
                        billed {rupees(p.invoiced_amount)}
                        {Number(p.credited_amount) > 0 && ` · credited ${rupees(p.credited_amount)}`}
                        {' · paid '}{rupees(p.paid_amount)}
                        {p.invoicing_closed ? ' · billing closed' : ' · billing still open'}
                      </>}
                      right={<>
                        <StatusPill status={p.status} />
                        <p className={`mt-0.5 text-xs font-semibold tabular-nums ${
                          Number(p.outstanding_amount) > 0.01 ? 'text-red-700' : 'text-emerald-700'}`}>
                          {rupees(p.outstanding_amount)} left
                        </p>
                      </>} />
              </div>
            ))}

            {trail.invoices.map((v) => (
              <div key={v.id}>
                <Rail />
                <Node icon={<ReceiptIndianRupee className="h-4 w-4" />} kind="Vendor invoice"
                      to={`/invoices/${v.id}`}
                      title={v.invoice_no}
                      meta={<>
                        {ddmmyyyy(v.invoice_date)}
                        {' · taxable '}{rupees(v.taxable_amount)}
                        {' · GST '}{rupees(v.gst_amount)}
                        {' · matched '}{rupees(v.matched_amount)}
                      </>}
                      right={<>
                        <StatusPill status={v.status} />
                        <p className="mt-0.5 text-xs font-semibold tabular-nums text-slate-700">
                          {rupees(v.total_amount)}
                        </p>
                      </>} />
              </div>
            ))}

            {trail.credit_notes.map((c) => (
              <div key={c.id}>
                <Rail />
                <Node icon={<ReceiptText className="h-4 w-4" />} kind="Credit note"
                      to="/credit-notes"
                      title={c.credit_no}
                      meta={<>{ddmmyyyy(c.credit_date)} · {c.reason}</>}
                      right={
                        <p className="text-xs font-semibold tabular-nums text-amber-700">
                          − {rupees(c.total_amount)}
                        </p>
                      } />
              </div>
            ))}

            {trail.payments.map((p) => (
              <div key={p.id}>
                <Rail />
                <Node icon={<Wallet className="h-4 w-4" />}
                      kind={p.payment_type === 'ADVANCE' ? 'Advance payment' : 'Payment'}
                      to="/payments"
                      title={p.id}
                      meta={<>
                        {ddmmyyyy(p.payment_date)} · {p.mode}
                        {p.reference_no && ` · ${p.reference_no}`}
                        {p.payment_type === 'ADVANCE' && Number(p.unadjusted_amount) > 0
                          && ` · ${rupees(p.unadjusted_amount)} not yet adjusted`}
                      </>}
                      right={
                        <p className="text-xs font-semibold tabular-nums text-emerald-700">
                          {rupees(p.amount)}
                        </p>
                      } />
              </div>
            ))}
          </div>

          <p className="mt-3 flex items-start gap-2 text-xs text-slate-500">
            <Paperclip className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300" />
            Every line links to that document&apos;s own screen. Nothing can be approved,
            edited or paid from here — this is a view of what happened, not a way to
            change it.
          </p>
        </>
      )}
    </Card>
  )
}
